import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="privacy-page">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-400 mb-8">Last updated: March 2026</p>

        <div className="prose prose-slate dark:prose-invert prose-sm max-w-none space-y-6">
          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">1. Introduction</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              INPW Technologies Private Limited ("we", "us", or "our") operates Inspiro Tracker. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">2. Information We Collect</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">We collect the following categories of information:</p>
            <ul className="list-disc pl-5 text-slate-600 dark:text-slate-400 space-y-1 mt-2">
              <li><strong>Account Information:</strong> Name, email address, organization name, role</li>
              <li><strong>Time Tracking Data:</strong> Time entries, project assignments, task descriptions, timesheets</li>
              <li><strong>Screenshots:</strong> When enabled by your workspace administrator, periodic screen captures during active time tracking</li>
              <li><strong>Billing Information:</strong> Payment details are processed securely via Stripe and Razorpay — we do not store card numbers</li>
              <li><strong>Usage Data:</strong> Browser type, device information, IP address, and interaction logs for service improvement</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 text-slate-600 dark:text-slate-400 space-y-1 mt-2">
              <li>To provide, maintain, and improve the Service</li>
              <li>To process payments and manage subscriptions</li>
              <li>To send transactional emails (verification, password resets, invoices)</li>
              <li>To provide customer support</li>
              <li>To enforce our Terms of Service and prevent abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">4. Data Storage & Security</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Your data is stored on secure servers in India (data region: in-1). We implement industry-standard security measures including encryption in transit (TLS/SSL), access controls, and regular security audits. Screenshots are retained for 30 days and then automatically deleted.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">5. Data Sharing</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              We do not sell, trade, or rent your personal information. We may share data with:
            </p>
            <ul className="list-disc pl-5 text-slate-600 dark:text-slate-400 space-y-1 mt-2">
              <li><strong>Payment processors:</strong> Stripe and Razorpay for billing</li>
              <li><strong>Email services:</strong> SendGrid for transactional emails</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">6. Screenshot Privacy</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Screen capture is an optional feature that must be explicitly enabled. Employees are notified before screenshots are taken. Workspace administrators can configure screenshot frequency and enable blurring for sensitive content. Users can disable screen sharing at any time via their browser.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">7. Your Rights</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">You have the right to:</p>
            <ul className="list-disc pl-5 text-slate-600 dark:text-slate-400 space-y-1 mt-2">
              <li>Access and export your personal data</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and associated data</li>
              <li>Opt out of non-essential communications</li>
              <li>Disable screenshot capture at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">8. Cookies</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              We use essential cookies and local storage to maintain your session, preferences, and authentication state. We do not use third-party tracking or advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">9. Children's Privacy</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The Service is not intended for users under 16 years of age. We do not knowingly collect information from children.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">10. Changes to This Policy</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              We may update this Privacy Policy periodically. We will notify you of material changes via email or in-app notification. The "Last updated" date at the top indicates the most recent revision.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">11. Contact Us</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              For privacy-related questions or requests, contact us at{' '}
              <a href="mailto:support@inspiroworks.com" className="text-blue-600 hover:text-blue-700 underline">support@inspiroworks.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
